import { api } from '@/lib/api/client'
import { limpaParams } from '@/services/shared/listParams'
import type { PageMeta } from '@/types/Page'
import { IContactInfo } from "@/types/IContactInfo";
import type { ContactDetail, ContactEventsPage, ContactProtocolItem, ContactState } from '@/types/Contact'

export async function insertNewContact(
    name: string,
    phoneNumber: string,
    email?: string,
    address?: string,
    parameters?: Record<string, string | number | boolean>[]
): Promise<IContactInfo> {

    const { data } = await api.post(`/contacts`, {
        name: name,
        phone_number: phoneNumber,
        email: email,
        address: address,
        parameters: parameters,
    });

    // Convert array of objects to single object for parameters if needed,
    // or just pass what we have if it matches.
    // Usually parameters from backend might be in a different format,
    // but based on IContactInfo usage, it expects an object map.
    // Let's assume for now we construct the IContactInfo from the input + id.

    // NOTE: The backend returns 'contact_id'. We need to be careful about what else it returns.
    // If the backend ONLY returns the ID, we have to construct the object manually using the inputs.

    const newContact: IContactInfo = {
        db_id: data._id,
        name: data.name,
        phone_number: data.phone_number,
        parameters: parameters ? parameters.reduce((acc, curr) => ({ ...acc, ...curr }), {}) : undefined,
        email: email || null,
        address: address || null,
        blocked: false, // Default value for new contact
        protocols: [] // Default value for new contact
    };

    return newContact;
}

export interface PatchContactFields {
    name?: string;
    email?: string | null;
    address?: string | null;
}

export async function patchContact(
    contactId: string,
    fields: PatchContactFields,
): Promise<void> {
    await api.patch(`/contacts/${contactId}`, fields);
}

/**
 * `PATCH /contacts/{id}/block?block=[&phone_id=]` — requer `contacts:block`;
 * **desbloquear exige `contacts:blocked:read` junto** (gate de estado do back).
 *
 * Sem `phone_id` a rota age em TODOS os números dos canais no escopo do usuário —
 * é AÇÃO em N números na mesma request, não um estado "bloqueado em todos".
 *
 * **Não decida pelo código HTTP**: 200 = tudo `synced`, 202 = algum pendente, e os
 * dois trazem o MESMO corpo — o `ContactDetail` completo, com `blocked_on[]`. É de
 * lá que a tela lê o que aconteceu em cada número (handoff §2).
 */
export async function blockContact(
    contactId: string,
    block: boolean,
    phoneId?: string,
): Promise<ContactDetail> {
    /* `phone_id` vazio é AUSÊNCIA, não filtro: `services/channels/index.ts` normaliza
       `phone_hash_id: null → ''`, e mandar `phone_id=''` pediria por um número que não
       existe (404/403) em vez de "todos os números do meu escopo". */
    const { data } = await api.patch<ContactDetail>(`/contacts/${contactId}/block`, null, {
        params: { block, ...(phoneId ? { phone_id: phoneId } : {}) },
    });
    return data;
}

export interface ContactsPage extends PageMeta {
  contacts: IContactInfo[]
}

/** Escalar continua válido no back — array é só a forma multiselect do MESMO filtro. */
type Multi<T extends string = string> = T | T[]

/**
 * Domínio fechado do filtro, em forma de valor.
 *
 * Existe como `const` (e não só como union de tipo) porque quem lê a URL precisa
 * validar em runtime: `?protocol_status=chute` é entrada de usuário e viraria 422
 * no back se passasse direto.
 */
export const PROTOCOL_STATUS_VALORES = ['active', 'finished', 'none'] as const

export type ProtocolStatus = (typeof PROTOCOL_STATUS_VALORES)[number]

export interface GetContactsParams {
  page?: number
  limit?: number
  search?: string
  /**
   * Os três valores PARTICIONAM o universo: todo contato é exatamente um deles.
   * `active` no wire é "não-finalizado" (`created` OU `active` no enum do back),
   * não `ProtocolStatus.ACTIVE` sozinho.
   *
   * Seleção **vazia** e seleção **completa** degeneram as duas para "sem filtro"
   * — é contrato, não acidente: uma UI de checkbox pode desmarcar tudo ou marcar
   * tudo, e nos dois casos o usuário quer ver tudo.
   */
  protocol_status?: Multi<ProtocolStatus>
  /** Contato com **≥1 protocolo** em algum dos canais — é predicado de PROTOCOLO, não de contato. */
  channel_id?: Multi
  /** TG **ativo e dentro do escopo do chamador**. Fora do escopo resolve conjunto vazio → 200 com lista vazia, nunca 403. */
  transmission_group_id?: Multi
  /** Tri-state: `true` / `false` / ausente. NÃO vira lista. */
  blocked?: boolean
  /** País do telefone, ISO alpha-2 (derivável do prefixo E.164 do phone_number). */
  country_code?: Multi
  /** DDD (código de área BR). Exige `BR` ENTRE os `country_code`, senão 422. */
  area_code?: Multi
  /** Ciclo de vida — ver `ContactState`. Ausente = `active` (o default é do back).
   *  Pedir `deleted`/`all` sem a `contacts:<estado>:read` é **403**, nunca filtro
   *  calado (`_ensure_state_access`). */
  state?: ContactState
  /**
   * Tri-state (`true` / `false` / ausente), **sem permission**: `contacts:read`
   * basta — não existe `contacts:unreachable:read`
   * (`app/permissions/permissions.py::ContactsPermissions`). Diferente de
   * `blocked`, que exige `contacts:blocked:read`: bloqueado/deletado escondem
   * decisão humana, e `unreachable` é fato da Meta.
   *
   * **`false` NÃO é ausência**: traz os não-marcados INCLUINDO o doc legado sem a
   * chave (`{'$ne': true}` no `$match`, `contacts/services/repository.py::_active_filter`).
   * Recorta DENTRO do `?state=`, como o `blocked`.
   */
  unreachable?: boolean
  /**
   * Tri-state (`true` / `false` / ausente), **sem permission** — back `76035100`.
   * `true` = `{wa_username: {$type: 'string'}}` (predicado exato do
   * `wa_username_idx` parcial — é o único recorte desta rota que o índice serve);
   * `false` = `{$not: {$type: 'string'}}`, pega `null` (como o BaseRepository
   * grava) E legado sem a chave. Recorta DENTRO do `?state=`; convive com
   * `search` (que casa o VALOR do username, no `$or`) por AND.
   * Sem facet: o domínio é livre.
   */
  has_wa_username?: boolean
  /**
   * **NÃO é tri-state** — é o único filtro desta rota que não é. `true` liga;
   * ausente desliga. `false` explícito o back APAGA
   * (`ContactFiltersSchema._available_only_normaliza_e_valida`), e não existe ramo
   * "só indisponíveis". Tipado `true` para que a forma errada nem seja
   * representável: `limpaParams` preserva `false` de propósito, então um `false`
   * viajaria e viraria ruído na URL do request e na queryKey.
   *
   * Semântica do back: colapsa em `blocked ≠ true AND unreachable ≠ true` DENTRO
   * do `?state=` (`manager.py::_resolve_filters`) — doc legado sem as chaves conta
   * como disponível (`{'$ne': True}`). NÃO lê a config `block_unreachable_sending`:
   * "disponível" é entrega possível (fato), não "o lote deixaria" (config).
   *
   * **Sem permission nova**: `_ensure_state_access` roda ANTES do colapso e só
   * gateia `blocked is True`; o recorte equivale a `blocked=false`, que é livre.
   *
   * **Nunca junto de `blocked: true` ou `unreachable: true`** — a combinação é 422
   * NATIVO do FastAPI (`detail` em ARRAY, SEM chave `error`), que o `parseApiError`
   * da casa rotula como VALIDATION genérico. Quem garante a exclusão é a UI
   * (`useContactsUrlState` e `ContactList`), não esta camada.
   *
   * **`total` do envelope REFLETE o filtro; `total_unfiltered` NÃO** — este é o
   * universo do `state` e conta bloqueados (`repository.py::count_all_active_contacts`).
   * "X de Y" com Y maior é o comportamento certo, não um bug a corrigir no client.
   */
  available_only?: true
}

/** GET /contacts paginada (§11). search é literal case-insensitive em name/phone_number/wa_username, escapado no back. */
export async function getContacts(params: GetContactsParams = {}): Promise<ContactsPage> {
  const { page = 1, limit = 50, state, ...resto } = params
  /* `archived`/`deleted` NÃO são params desta rota — `deleted` saiu do contrato no
     P2 (back `01790523`), e `archived` morreu junto com arquivar contato. O FastAPI
     DESCARTA param desconhecido em silêncio, sem 422: um call site que não migrou
     tem que virar no-op AQUI, não filtro fantasma no back. O TypeScript já barra os
     dois, mas isto é a defesa de quem chega por JS ou por objeto montado em runtime.
     O `delete` é na cópia que o rest já fez, nunca no objeto do caller. */
  const filtros = resto as Record<string, unknown>
  delete filtros.archived
  delete filtros.deleted
  const { data } = await api.get<ContactsPage>('/contacts', {
    params: {
      page,
      limit,
      // `active` é o default do schema do back: não emitir mantém o link limpo
      // e o default num lugar só.
      ...(state && state !== 'active' ? { state } : {}),
      ...limpaParams(filtros),
    },
  })
  return data
}

/** Chaves únicas que alimentam os seletores de origem. */
export interface ContactFacets {
  /** ISO alpha-2 dos países presentes no tenant. */
  country_codes: string[]
  /** DDDs distintos — só de números BR. */
  area_codes: string[]
}

/**
 * GET /contacts/filters — o universo de opções dos filtros de origem.
 *
 * A listagem é paginada, então o front NUNCA enxerga o universo completo: derivar as
 * opções das páginas carregadas dá sets incompletos, e derivar da resposta já filtrada
 * faz o filtro se apagar (escolher BR → resposta só BR → some todo o resto do select).
 * Por isso as facets vêm daqui, computadas no back SEM search e SEM filtros — ficam
 * estáveis enquanto o usuário digita e são cacheáveis no client.
 */
export async function getContactFacets(): Promise<ContactFacets> {
  const { data } = await api.get<ContactFacets>('/contacts/filters')
  return data
}

/**
 * INTERIM até o back expor busca por ids (pendência 24e): resolve contatos por db_id
 * paginando a lista (teto 200/página) com cap de páginas. Uso pontual (nomes de membros
 * de TG) — NÃO usar como fonte de listagem.
 */
export async function resolveContactsByIds(ids: string[], maxPages = 10): Promise<Map<string, IContactInfo>> {
  const wanted = new Set(ids)
  const found = new Map<string, IContactInfo>()
  let currentPage = 1
  while (found.size < wanted.size && currentPage <= maxPages) {
    const { contacts, total, page: pageNum, limit } = await getContacts({ page: currentPage, limit: 200 })
    for (const contact of contacts) {
      if (wanted.has(contact.db_id)) found.set(contact.db_id, contact)
    }
    if (pageNum * limit >= total) break
    currentPage++
  }
  return found
}

/** `GET /contacts/{id}` — requer `contacts:read` + a permission de estado (ver `_state_filter`
 *  no back) quando o contato está bloqueado/deletado; sem ela, 403. */
export async function getContactDetail(contactId: string): Promise<ContactDetail> {
  const { data } = await api.get<ContactDetail>(`/contacts/${contactId}`)
  return data
}

/** `GET /contacts/{id}/protocols` — requer `contacts:read` (o manager decora
 *  `list_contact_protocols` com `ContactsPermissions.READ`) + gate de estado. Só
 *  `/events` exige `contacts:history:read`. NÃO é `PageEnvelope`:
 *  o back devolve `{ protocols: [...] }`, sem paginação. */
export async function getContactProtocols(contactId: string): Promise<ContactProtocolItem[]> {
  const { data } = await api.get<{ protocols: ContactProtocolItem[] }>(`/contacts/${contactId}/protocols`)
  return data.protocols
}

/** `GET /contacts/{id}/events` — requer `contacts:history:read`. É `PageEnvelope`, chave `events`. */
export async function getContactEvents(
  contactId: string,
  params: { page?: number; limit?: number } = {},
): Promise<ContactEventsPage> {
  const { data } = await api.get<ContactEventsPage>(`/contacts/${contactId}/events`, { params })
  return data
}

/** `POST /contacts/{id}/restore` — requer `contacts:delete`. NÃO exige `contacts:deleted:read`:
 *  `restore` é a única exceção do gate — `_ensure_contact_mutable(restoring=True)` descarta
 *  `deleted` do conjunto, porque é a pré-condição da operação. Se o contato também estiver
 *  bloqueado, a `blocked:read` continua valendo. Sem body. */
export async function restoreContact(contactId: string): Promise<ContactDetail> {
  const { data } = await api.post<ContactDetail>(`/contacts/${contactId}/restore`)
  return data
}

/**
 * `POST /contacts/{id}/clear_unreachable` — desmarca "inalcançável" à mão. Requer
 * `contacts:unreachable:clear`, que é **runtime-only** (fora de todo preset do
 * back): o botão NASCE 403 até o grant existir, e isso é ESTADO, não bug.
 *
 * Sem body; devolve o `ContactDetail` novo, mesmo shape do `restore`.
 * **200 idempotente** em contato não marcado (sem evento). Passa pelo mesmo gate de
 * estado das outras mutações (`_ensure_contact_mutable`): contato bloqueado exige
 * a `contacts:blocked:read`, senão 403. Contato
 * deletado é **409 `CONTACT_INACTIVE`** — e só chega a 409 quem tem
 * `contacts:deleted:read`; sem ela o 403 vem antes.
 *
 * A flag é reversível por outros DOIS caminhos além deste, ambos com ator `System`:
 * qualquer inbound do contato, e `delivered`/`read` de qualquer envio. `sent` NÃO
 * desmarca — diz que a Meta aceitou, não que chegou.
 */
export async function clearUnreachableContact(contactId: string): Promise<ContactDetail> {
  const { data } = await api.post<ContactDetail>(`/contacts/${contactId}/clear_unreachable`)
  return data
}

/** `DELETE /contacts/{id}` — requer `contacts:delete` (a MESMA do restore) e passa
 *  pelo gate de estado: deletar contato bloqueado exige a `contacts:blocked:read`. Soft delete — o back só faz `$set {active: false}`, nada é reconstruído
 *  no `restore`.
 *
 *  `Promise<void>` de propósito: a rota **não é pinada por response model** (sem
 *  `-> ContactDetailResponse`, sem o helper `_detail`, ao contrário do
 *  `restore`), então o corpo é o domain serializado pelo default do
 *  FastAPI — superset não validado que muda em silêncio se `ContactModel` mudar.
 *  `blockContact` **deixou** essa política no arco A2: a rota dele é pinada por
 *  `ContactDetailResponse` e o corpo passou a ser consumido. */
export async function deleteContact(contactId: string): Promise<void> {
  await api.delete(`/contacts/${contactId}`)
}
